import { useCallback, useEffect, useMemo, useState } from 'react';

import type { ProductHit } from '@libs/algolia';
import type { Product } from '@app/pdp/types';
import type { FBTProduct } from '../types';

import { useQueryFBT } from './useQueryFBT';
import { useQueryStockInfoForIds } from './useQueryStockInfoForIds';

export const filterProducts = (products: ProductHit[]) => {
    const uniqueIds = new Set();
    const filteredProducts = [];

    for (const product of products) {
        if (!uniqueIds.has(product.id)) {
            uniqueIds.add(product.id);
            filteredProducts.push(product);
        }
    }

    return filteredProducts;
};

type UseFBTProps = {
    product: Product;
    customerId: string;
    storeId: string | undefined;
    isEnabled: boolean;
};

export const useFBT = ({
    product,
    customerId,
    storeId,
    isEnabled,
}: UseFBTProps) => {
    // 1) Cached FBT products data
    const { fbtProducts, isSuccess } = useQueryFBT({
        storeId,
        customerId,
        productId: product.objectID,
        isEnabled: isEnabled && Boolean(product.objectID) && Boolean(storeId),
    });

    // Preparing to fetch the stockTable
    const parsedStoreId =
        typeof storeId === 'string' ? parseInt(storeId, 10) : storeId;

    // useMemo needs because allProducts is set as dependency for productRecommendations bellow
    const allProducts = useMemo(
        () => filterProducts([product, ...(fbtProducts ?? [])]),
        [product, fbtProducts]
    );

    const productIds = allProducts.map(productHit => productHit.id);

    // 2) Cached stock table data
    const { data: stockTable } = useQueryStockInfoForIds({
        productIds,
        storeId: parsedStoreId,
        isEnabled:
            isEnabled &&
            isSuccess &&
            Boolean(storeId) &&
            Boolean(fbtProducts?.length),
    });

    // useMemo needs because productRecommendations is set as a dependency in useEffect below to prevent infinity rerender
    const productRecommendations = useMemo(
        () =>
            allProducts.map(productHit => ({
                ...productHit,
                in_stock: Boolean(stockTable?.[productHit.id]),
                isSelected: Boolean(stockTable?.[productHit.id]),
            })),
        [allProducts, stockTable]
    );

    // State of products
    const [recommendedProducts, setRecommendedProducts] = useState<
        FBTProduct[]
    >([]);

    useEffect(() => {
        // Memorized productRecommendations prevents infinite call to useEffect
        setRecommendedProducts(productRecommendations);
    }, [productRecommendations]);

    const handleSelectProduct = useCallback(
        (isChecked: boolean, index: number) => {
            setRecommendedProducts(prevState => {
                const newProducts = [...prevState];
                newProducts[index].isSelected = isChecked;

                return newProducts;
            });
        },
        [setRecommendedProducts]
    );

    return { recommendedProducts, handleSelectProduct };
};
